import { Injectable } from '@angular/core';
import { AppConfig } from '../app.config';
import { environment } from '../../environments/environment';
import { SessionTileParams } from '../_models/session';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseService } from './base/base.service';
import { PlacementMaintenance } from '../_models/maintenance/placement-maintenance.model';
import { Router } from '@angular/router';
import { SessionUserHashManagerService } from './session-user-hash-manager/session-user-hash-manager.service';
import { PagedRequest } from '../_models/common/paged-response.model';

@Injectable()
export class SessionService extends BaseService {
  public sessionDataParamToday: SessionTileParams = new SessionTileParams();
  public sessionDataParamThisWeek: SessionTileParams = new SessionTileParams();
  public sessionDataParamLastWeek: SessionTileParams = new SessionTileParams();
  public sessionDataParamNextWeek: SessionTileParams = new SessionTileParams();
  
  private apiUrl: string = environment.apiUrl;

  constructor(
    private http: HttpClient, 
    private config: AppConfig,
    protected hashManagerService: SessionUserHashManagerService
    ) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith("/") ? "" : "/";
  }

  /**
   * Searches sessions. `paging` is opt-in: omit it (the default) to get today's full, unpaged
   * array back - `sessions.tile.component.ts` (dashboard/homepage preview tiles) and
   * `sessions.tutor.homepage.ts` (this-week/last-week summary widget) rely on this and must keep
   * calling with no second argument; neither is a table and both need every matching row (see
   * Plans/table-lazy-loading.md §7c's `GetSessionsV2` subsection). Pass it only from
   * `sessions.search.component.ts`, the screen converted to server-side paging
   * (Plans/table-lazy-loading.md §4 A2) - it then receives a `PagedResponse<SessionGroup>` instead
   * of the bare array.
   */
  /**
   * The sessions tile's own slim read — reads LeapDb's cached schedule and returns only the columns the
   * tile renders. Replaces calling searchSessions (GetSessionsV2) from the tile, which fetched 29 fields
   * per row from Orbit. See TutorPortalAPI/Plans/table-lazy-loading.md §7d.
   */
  getSessionTileRows(request: { startDate: any; endDate: any; tutorIds: number[] }) {
    return this.http.post(this.apiUrl + 'api/session/SessionTileRows', request, this.prepareOptions(request)).pipe(
      map((data: any) => {
        return data.body;
      }), catchError(this.handleError('getSessionTileRows'))
    );
  }

  searchSessions(searchParams, paging?: PagedRequest) {
    if (paging) {
      searchParams.paged = true;
      searchParams.pageNumber = paging.pageNumber;
      searchParams.pageSize = paging.pageSize;
      searchParams.sortField = paging.sortField;
      searchParams.sortDirection = paging.sortDirection;
    }
    return this.http.post(this.apiUrl + 'api/session/GetSessionsV2', searchParams, this.prepareOptions(searchParams)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('searchSessions'))
    );

    // return this.simulateError().pipe(
    //   map((data: any) => {
    //     if (data.status === 299) {
    //       throw {
    //         message: data.body,
    //         status: data.status,
    //         isValidationError: true
    //       };
    //     } else if (data.name && data.name == "HttpErrorResponse") {
    //       throw {
    //         message: data.body,
    //         status: data.status,
    //         isValidationError: false
    //       };
    //     } else {
    //       return data.body;
    //     }
    //   }), catchError(this.handleError('searchSessions')));
  }

  getPlacements(data) {
    return this.http.post(this.apiUrl + 'api/session/GetPlacements', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getPlacements'))
    );
  }

  public getSessionBlocks(placementId: number, startDate: Date, endDate: Date) {
    return this.http.post(this.apiUrl + 'api/session/GetFullSessionByPlacement/', 
    {
      "placementId": placementId,
      "startDate": startDate,
      "endDate": endDate
    }, this.prepareOptions({"placementId": placementId,"startDate": startDate,"endDate": endDate})).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getSessionBlocks'))
    );
  }

  getSessionTileDates(today) {
    var firstday = this.startOfWeek(today);
    var lastday = this.addDays(firstday, 6);

    let lastWeekSunday = this.addDays(firstday, -7);
    let lastWeekSaturday = this.addDays(lastday, -7);

    let nextWeekSunday = this.addDays(firstday, 7);
    let nextWeekSaturday = this.addDays(lastday, 7);

    this.sessionDataParamLastWeek = {
      sessionHeader: 'Last Week',
      startDate: lastWeekSunday,
      endDate: lastWeekSaturday,
      excludeToday: false,
      mode: true,
      sessionNodata : 'There were no sessions last week'
    }

    this.sessionDataParamToday = {
      sessionHeader: 'Today',
      startDate: new Date(),
      endDate: new Date(),
      excludeToday: false,
      mode: true,
      sessionNodata: 'There are no sessions today'
    }

    this.sessionDataParamThisWeek = {
      sessionHeader: 'Other days this week',
      startDate: firstday,
      endDate: lastday,
      excludeToday: true,
      mode: true,
      sessionNodata: 'There are no sessions this week'
    }

    this.sessionDataParamNextWeek = {
      sessionHeader: 'Next Week',
      startDate: nextWeekSunday,
      endDate: nextWeekSaturday,
      excludeToday: false,
      mode: true,
      sessionNodata: 'There are no sessions next week'
    }
  }

  addDays(date, days) {
    var result = new Date(date);
    result.setDate(result.getDate() + days);
    result.setHours(2, 0, 0, 0);
    return result;
  }

  startOfWeek(date) {
    var diff = date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1);
    let result = new Date(date.setDate(diff));
    result.setHours(2, 0, 0, 0);
    return result;
  }
}
